import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Check, X, Trash2, User, Mail, Phone, MapPin, Loader2, MessageSquare } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRequests = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRequests = async (status) => {
    setLoading(true);
    try {
      const url = status === 'all' 
        ? `${API_URL}/admin/service-requests`
        : `${API_URL}/admin/service-requests?status=${status}`;
      const response = await axios.get(url, { headers });
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(filter);
  }, [filter]);

  const handleApprove = async (requestId) => {
    try {
      await axios.patch(`${API_URL}/admin/service-requests/${requestId}/approve`, {}, { headers });
      toast.success('Request approved! Service provider has been added.');
      fetchRequests(filter);
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await axios.patch(`${API_URL}/admin/service-requests/${requestId}/reject`, {}, { headers });
      toast.success('Request rejected');
      fetchRequests(filter);
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Delete this request permanently?')) return;
    try {
      await axios.delete(`${API_URL}/admin/service-requests/${requestId}`, { headers });
      toast.success('Request deleted');
      fetchRequests(filter);
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const getServiceTypeLabel = (type) => {
    const labels = {
      teacher: 'Teacher',
      katha: 'Katha Speaker',
      event: 'Event Organizer',
      live_stream: 'Live Stream Provider'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div data-testid="admin-requests">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-gray-900">Service Requests</h1>
        <p className="text-gray-600">Review and approve collaboration requests from community members</p>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-saffron-500" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No {filter} requests</h3>
            <p className="text-gray-500">
              {filter === 'pending' 
                ? 'All caught up! No pending requests to review.'
                : 'No requests in this category.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <Card key={request.id} className="hover:shadow-md transition-shadow" data-testid={`request-${request.id}`}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-royal-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-royal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">{request.name}</h3>
                      <p className="text-saffron-600 font-medium">{getServiceTypeLabel(request.service_type)}</p>
                    </div>
                  </div>
                  {getStatusBadge(request.status)}
                </div>

                <p className="text-gray-600 mb-4">{request.description}</p>

                <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {request.email}
                  </span>
                  {request.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {request.phone}
                    </span>
                  )}
                  {request.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {request.location}
                    </span>
                  )}
                </div>

                {request.expertise && request.expertise.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {request.expertise.map((skill, i) => (
                      <Badge key={i} variant="outline" className="bg-gray-50">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t">
                  {request.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => handleApprove(request.id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`approve-${request.id}`}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleReject(request.id)}
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`reject-${request.id}`}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleDelete(request.id)}
                    className="ml-auto text-gray-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminRequests;
